import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PlayerAvatar from "../components/PlayerAvatar";
import { CheckCircle, XCircle, Clock, Bandaids, CalendarBlank } from "@phosphor-icons/react";

const STATUS_BUTTONS = [
  { key: "present", label: "Present", icon: CheckCircle, bg: "#06D6A0", fg: "#050505" },
  { key: "absent", label: "Absent", icon: XCircle, bg: "#D90429", fg: "#ffffff" },
  { key: "late", label: "Late", icon: Clock, bg: "#FFD166", fg: "#050505" },
  { key: "injured", label: "Injured", icon: Bandaids, bg: "#F97316", fg: "#050505" },
];

export default function Attendance() {
  const [players, setPlayers] = useState([]);
  const [settings, setSettings] = useState({});
  const [team, setTeam] = useState("");
  const [ageGroup, setAgeGroup] = useState("");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [sessionType, setSessionType] = useState("practice");
  const [records, setRecords] = useState({}); // player_id -> status

  async function loadRecords(d) {
    const r = await api.get(`/attendance?date=${d}`);
    const map = {};
    r.data.forEach((a) => {
      if (a.session_type === sessionType) map[a.player_id] = a.status;
    });
    setRecords(map);
  }

  useEffect(() => {
    api.get("/players").then((r) => setPlayers(r.data));
    api.get("/settings").then((r) => setSettings(r.data || {}));
  }, []);
  useEffect(() => { loadRecords(date); /* eslint-disable-next-line */ }, [date, sessionType]);

  async function mark(playerId, status) {
    setRecords((r) => ({ ...r, [playerId]: status })); // optimistic
    try {
      await api.post("/attendance", { player_id: playerId, date, status, session_type: sessionType });
    } catch (e) {
      toast.error(formatApiError(e));
      loadRecords(date);
    }
  }

  const filteredPlayers = useMemo(() => {
    return players.filter((p) =>
      (!team || p.team === team) && (!ageGroup || p.age_group === ageGroup)
    );
  }, [players, team, ageGroup]);

  async function markAllPresent() {
    const targets = filteredPlayers.filter((p) => records[p.id] !== "present");
    if (targets.length === 0) { toast("Everyone is already present"); return; }
    const optimistic = { ...records };
    targets.forEach((p) => { optimistic[p.id] = "present"; });
    setRecords(optimistic);
    try {
      await Promise.all(targets.map((p) => api.post("/attendance", {
        player_id: p.id, date, status: "present", session_type: sessionType,
      })));
      toast.success(`Marked ${targets.length} present`);
    } catch (e) {
      toast.error(formatApiError(e));
      loadRecords(date);
    }
  }

  const summary = useMemo(() => {
    const s = { present: 0, absent: 0, late: 0, injured: 0, unmarked: 0 };
    filteredPlayers.forEach((p) => {
      const r = records[p.id];
      if (!r) s.unmarked++;
      else s[r]++;
    });
    return s;
  }, [filteredPlayers, records]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="overline">One-tap</div>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-1">Attendance</h1>
        </div>
        <button
          onClick={markAllPresent}
          data-testid="mark-all-present-btn"
          disabled={filteredPlayers.length === 0}
          className="h-11 px-4 rounded-md bg-[#06D6A0] hover:bg-[#04b587] text-[#050505] font-display font-bold uppercase tracking-wider text-xs inline-flex items-center gap-2 disabled:opacity-50"
        >
          <CheckCircle size={16} weight="fill" /> Mark all present
        </button>
      </div>

      <div className="bg-[#121212] border border-neutral-800 rounded-lg p-4 grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="flex flex-col">
          <span className="overline mb-1">Date</span>
          <div className="relative">
            <CalendarBlank size={20} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="date"
              data-testid="attendance-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="w-full h-12 pl-10 pr-3 bg-[#0a0a0a] border border-neutral-800 rounded-md focus:outline-none focus:border-[#D90429]"
            />
          </div>
        </label>
        <label className="flex flex-col">
          <span className="overline mb-1">Session</span>
          <select
            data-testid="attendance-session"
            value={sessionType}
            onChange={(e) => setSessionType(e.target.value)}
            className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="practice">Practice</option>
            <option value="match">Match</option>
          </select>
        </label>
        <label className="flex flex-col">
          <span className="overline mb-1">Team</span>
          <select
            data-testid="attendance-filter-team"
            value={team}
            onChange={(e) => setTeam(e.target.value)}
            className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="">All Teams</option>
            {(settings.teams || []).map((t) => <option key={t}>{t}</option>)}
          </select>
        </label>
        <label className="flex flex-col">
          <span className="overline mb-1">Age Group</span>
          <select
            data-testid="attendance-filter-age-group"
            value={ageGroup}
            onChange={(e) => setAgeGroup(e.target.value)}
            className="w-full h-12 px-3 bg-[#0a0a0a] border border-neutral-800 rounded-md"
          >
            <option value="">All Age Groups</option>
            {(settings.age_groups || []).map((a) => <option key={a}>{a}</option>)}
          </select>
        </label>
        <div className="col-span-2 md:col-span-4 grid grid-cols-4 gap-2 pt-2 border-t border-neutral-900">
          <SummaryChip label="Present" value={summary.present} color="#06D6A0" />
          <SummaryChip label="Absent" value={summary.absent} color="#D90429" />
          <SummaryChip label="Late" value={summary.late} color="#FFD166" />
          <SummaryChip label="Injured" value={summary.injured} color="#F97316" />
        </div>
      </div>

      {filteredPlayers.length === 0 ? (
        <div className="bg-[#121212] border border-neutral-800 rounded-lg p-10 text-center overline">
          {players.length === 0 ? "Add players first" : "No players match the current filter"}
        </div>
      ) : (
        <div className="space-y-3">
          {filteredPlayers.map((p) => {
            const current = records[p.id];
            return (
              <div key={p.id} className="bg-[#121212] border border-neutral-800 rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-4">
                <div className="flex items-center gap-3 md:w-64">
                  <PlayerAvatar player={p} size={48} />
                  <div>
                    <div className="font-display font-bold uppercase tracking-tight leading-tight">
                      {p.first_name} {p.last_name}
                    </div>
                    <div className="overline">#{p.jersey_number || "—"} • {p.position || "—"}</div>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 flex-1">
                  {STATUS_BUTTONS.map((b) => {
                    const active = current === b.key;
                    return (
                      <button
                        key={b.key}
                        data-testid={`attendance-${b.key}-${p.id}`}
                        onClick={() => mark(p.id, b.key)}
                        className={`h-14 rounded-md font-bold uppercase tracking-wider text-xs flex flex-col items-center justify-center gap-1 transition-colors border ${
                          active
                            ? "border-transparent"
                            : "border-neutral-800 hover:border-neutral-700 bg-[#0a0a0a] text-neutral-400"
                        }`}
                        style={active ? { background: b.bg, color: b.fg } : {}}
                      >
                        <b.icon size={20} weight={active ? "fill" : "bold"} />
                        {b.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

const SummaryChip = ({ label, value, color }) => (
  <div className="text-center">
    <div className="overline">{label}</div>
    <div className="font-display font-black text-xl" style={{ color }}>{value}</div>
  </div>
);
